import React, { useEffect, useState } from "react";
import {
  Box, Button, Paper, TextField, Typography, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, Switch,
  IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import LockResetIcon from "@mui/icons-material/LockReset";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  inviteUser,
  listOrgUsers,
  updateOrgUser,
  deleteOrgUser,
  resetUserPasswordEmail,
} from "../../services/settings_services";

const roleOptions = [
  { value: "organization-user", label: "Organization User" },
  { value: "organization-admin", label: "Organization Admin" },
];

const OrgUsersTab = () => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("organization-user");
  const [busy, setBusy] = useState(false);

  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [userToDelete, setUserToDelete] = useState(null);

  const load = async () => {
    setError("");
    try {
      const res = await listOrgUsers();
      setUsers(res.items || []);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load users");
    }
  };

  useEffect(() => { load(); }, []);

  const onInvite = async () => {
    setBusy(true);
    setError("");
    try {
      await inviteUser({ email, role });
      setEmail("");
      setRole("organization-user");
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  const onToggleEnabled = async (u) => {
    setBusy(true);
    setError("");
    try {
      await updateOrgUser(u.id, { enabled: !u.enabled });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const onChangeRole = async (u, newRole) => {
    setBusy(true);
    setError("");
    try {
      await updateOrgUser(u.id, { role: newRole });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Role update failed");
    } finally {
      setBusy(false);
    }
  };

  const onResetPasswordEmail = async (u) => {
    setBusy(true);
    setError("");
    try {
      await resetUserPasswordEmail(u.id);
    } catch (e) {
      setError(e?.response?.data?.error || "Reset email failed");
    } finally {
      setBusy(false);
    }
  };

  const onDeleteConfirmed = async () => {
    const u = userToDelete;
    setUserToDelete(null);
    if (!u) return;
    setBusy(true);
    setError("");
    try {
      await deleteOrgUser(u.id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Invite user
        </Typography>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="small"
            sx={{ minWidth: 280 }}
          />

          <TextField
            label="Role"
            select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            size="small"
            sx={{ minWidth: 220 }}
          >
            {roleOptions.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            disabled={busy || !email.includes("@")}
            onClick={onInvite}
          >
            Send invite
          </Button>

          <Tooltip title="Refresh">
            <IconButton onClick={load} disabled={busy}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Users
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="center">Enabled</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.username || "-"}</TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    value={u.role}
                    onChange={(e) => onChangeRole(u, e.target.value)}
                    disabled={busy}
                    sx={{ minWidth: 220 }}
                  >
                    {roleOptions.map((r) => (
                      <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell align="center">
                  <Switch checked={!!u.enabled} onChange={() => onToggleEnabled(u)} disabled={busy} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Send reset password email">
                    <span>
                      <IconButton onClick={() => onResetPasswordEmail(u)} disabled={busy}>
                        <LockResetIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete user">
                    <span>
                      <IconButton
                        onClick={() => setUserToDelete(u)}
                        disabled={busy}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2">
                    No users found for this organization.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        open={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        aria-labelledby="delete-user-dialog-title"
      >
        <DialogTitle id="delete-user-dialog-title">Delete user</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{userToDelete?.email}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserToDelete(null)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onDeleteConfirmed} disabled={busy} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrgUsersTab;
